import React, { useState, useEffect, useLayoutEffect, useRef } from "react";

// react-router
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";

// ui
import { Backdrop, Button, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import poTheme from "./lib/poTheme";

import { RequestLoadingModal } from "./component/RequestLoadingModal";
import { SessionReloginModal } from "./component/SessionReloginModal";
import { PoToast } from "./component/PoToast";

// jotai
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { AppAtoms } from "./lib/store";
import { useWindowSize } from "./lib/useWindowSize";

// api
import axios from "axios";
import { getProfile } from "./api/profile";
import { getScreenColumns } from "./api/screenColumn";

// 画面一覧
import Account from "./pages/Account";
import AccountRunking from "./pages/AccountRunking";
import Contract from "./pages/Contract";
import Incentive from "./pages/Incentive";
import Product from "./pages/Product";
import ProductPartnerResult from "./pages/ProductPartnerResult";
import TossUp from "./pages/TossUp";
import TossUpForm from "./pages/TossUpForm";
import EntryForm from "./pages/EntryForm";
import Service from "./pages/Service";
import SaleCompany from "./pages/SaleCompany";
import Dashboard from "./pages/Dashboard";
import DataAnalysis from "./pages/DataAnalysis";
import FollowCall from "./pages/FollowCall";
import FollowCallConfig from "./pages/FollowCallConfig";
import ScreenColumnConfig from "./pages/ScreenColumnConfig";
import OutsideEntrySite from "./pages/OutsideEntrySite";
import OutsideEntryForm from "./pages/OutsideEntryForm";
import SystemConfig from "./pages/SystemConfig";
import Inquiry from "./pages/Inquiry";
/*
import ProductManual from "./pages/ProductManual";
*/
import Payment from "./pages/Payment";
import PaymentConfirm from "./pages/PaymentConfirm";

import Login from "./pages/Login";

// コンポーネント
import AppShell from "./component/shell/AppShell";
import { CONST, isProductionEnv } from "./config/const";

// 認証確認
import { fetchAuth, checkAuth } from "./lib/auth";

type GuardOpts = {
  allowAuthority?: string[];
  denyDbAuthority?: string[];
  denyProductionEnv?: boolean;
};

function Guard({
  opts,
  children,
}: {
  opts: GuardOpts;
  children: React.ReactNode;
}) {
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const profileLoaded = useAtomValue(AppAtoms.profileLoaded);

  if (opts.denyProductionEnv && isProductionEnv) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!loginUser?.authority)
    return (
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={true}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  if (
    opts.allowAuthority &&
    !opts.allowAuthority.includes(loginUser.authority)
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (opts.denyDbAuthority) {
    if (
      profileLoaded &&
      loginUser.db_authority &&
      opts.denyDbAuthority.includes(loginUser.db_authority)
    ) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

const useLatest = (value: any) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};
function App() {
  const [isLogin, setIsLogin] = useAtom(AppAtoms.isLogin);
  const lastIsLogin = useLatest(isLogin);
  const [loginUser, setLoginUser] = useAtom(AppAtoms.loginUser);
  const isLoading = useAtomValue(AppAtoms.isLoading);
  const setIsSmartPhone = useSetAtom(AppAtoms.isSmartPhone);
  const setPathname = useSetAtom(AppAtoms.pathname);
  const setContractScreenColumn = useSetAtom(AppAtoms.contractScreenColumn);
  const setTossUpScreenColumn = useSetAtom(AppAtoms.tossUpScreenColumn);
  const setAccountRunkingScreenColumn = useSetAtom(
    AppAtoms.accountRunkingScreenColumn,
  );
  const setTenantPlan = useSetAtom(AppAtoms.tenantPlanAtom);
  const setProfileLoaded = useSetAtom(AppAtoms.profileLoaded);
  const [, setSampleQuestions] = useAtom(AppAtoms.sampleQuestionsAtom);
  const { windowWidth, windowHeight } = useWindowSize();

  const [firstLoading, setFirstLoading] = useState(true);
  const [requestLoading, setRequestLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const location = useLocation();
  const checkSession = async () => {
    if (lastIsLogin.current) {
      const serviceVersion = await checkAuth();
      if (serviceVersion === "session_expired") {
        // セッション切れ: SessionReloginModalを表示 (cmd-po_1102 U1)
        setSessionExpired(true);
      } else if (CONST.serviceVersion !== serviceVersion) {
        // 画面更新要求
        setRequestLoading(true);
      }
    }
  };
  useEffect(() => {
    async function getScreenColumn() {
      const {
        contractScreenColumn,
        tossUpScreenColumn,
        accountRunkingScreenColumn,
      } = await getScreenColumns();
      setContractScreenColumn(contractScreenColumn);
      setTossUpScreenColumn(tossUpScreenColumn);
      setAccountRunkingScreenColumn(accountRunkingScreenColumn);
    }
    async function checkFetchAuth() {
      const { login, loginUser } = await fetchAuth();
      if (loginUser) {
        setLoginUser(loginUser);
        setIsLogin(login);
        getScreenColumn();
        axios
          .get(CONST.apiUrl2 + "sampleQuestions")
          .then((res) => {
            if (res.data.questions_by_page) {
              setSampleQuestions(res.data.questions_by_page);
            }
          })
          .catch(() => {});
        setFirstLoading(false);
        try {
          const profile = await getProfile();
          setLoginUser({
            ...loginUser,
            db_authority: profile.authority,
            username: profile.account_name,
          });
          // プラン情報をatomにセット
          setTenantPlan(
            profile.plan_id != null
              ? {
                  plan_id: profile.plan_id,
                  plan_name: profile.plan_name ?? "",
                  max_partners: profile.max_partners ?? 0,
                  max_et_sites: profile.max_et_sites ?? 0,
                  has_payment: profile.has_payment ?? false,
                  has_ai_analysis: profile.has_ai_analysis ?? false,
                  current_partners: profile.current_partners ?? 0,
                  current_et_sites: profile.current_et_sites ?? 0,
                  monthly_price: profile.monthly_price ?? 0,
                }
              : null,
          );
        } finally {
          setProfileLoaded(true);
        }
      }
      setFirstLoading(false);
      checkSession();
    }
    checkFetchAuth();
    setPathname(location.pathname);
  }, []);
  useLayoutEffect(() => {
    const isSmartPhone =
      windowWidth < 400 ||
      !!navigator.userAgent.match(/iPhone|Android.+Mobile/);
    setIsSmartPhone(isSmartPhone);
  }, [windowWidth]);

  const navigate = useNavigate();
  useEffect(() => {
    // 60秒ごとにチェック
    const intervalId = setInterval(checkSession, 60000);
    // コンポーネントのアンマウント時にクリーンアップ
    return () => clearInterval(intervalId);
  }, [navigate]);

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {firstLoading ? null : isLogin ? (
        <ThemeProvider theme={poTheme}>
          <CssBaseline />
          <RequestLoadingModal onModal={requestLoading} />
          <SessionReloginModal
            open={sessionExpired}
            onSuccess={() => setSessionExpired(false)}
          />
          <PoToast />
          <AppShell>
            <Routes>
              {/* ===== 18 live routes ===== */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/toss_up" element={<TossUp />} />
              <Route
                path="/follow_call"
                element={
                  <Guard opts={{ denyDbAuthority: ["トスアップアカウント"] }}>
                    <FollowCall />
                  </Guard>
                }
              />
              <Route
                path="/contract"
                element={
                  <Guard opts={{ denyDbAuthority: ["トスアップアカウント"] }}>
                    <Contract />
                  </Guard>
                }
              />
              <Route
                path="/runking"
                element={
                  <Guard
                    opts={{
                      denyDbAuthority: [
                        "営業アカウント",
                        "トスアップアカウント",
                      ],
                    }}
                  >
                    <AccountRunking />
                  </Guard>
                }
              />
              <Route
                path="/product_partner_result"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <ProductPartnerResult />
                  </Guard>
                }
              />
              <Route
                path="/data-analysis"
                element={
                  <Guard opts={{ denyProductionEnv: true }}>
                    <DataAnalysis />
                  </Guard>
                }
              />
              <Route
                path="/payment_admin"
                element={
                  <Guard opts={{ allowAuthority: ["マスターアカウント"] }}>
                    <Payment />
                  </Guard>
                }
              />
              <Route
                path="/payment"
                element={
                  <Guard
                    opts={{
                      allowAuthority: ["マスターアカウント", "パートナー"],
                    }}
                  >
                    <PaymentConfirm />
                  </Guard>
                }
              />
              <Route
                path="/entry_form"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <EntryForm />
                  </Guard>
                }
              />
              <Route
                path="/toss_up_form"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <TossUpForm />
                  </Guard>
                }
              />
              <Route
                path="/outside_entry_form"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <OutsideEntryForm />
                  </Guard>
                }
              />
              <Route
                path="/product"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <Product />
                  </Guard>
                }
              />
              <Route
                path="/service"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <Service />
                  </Guard>
                }
              />
              <Route
                path="/incentive"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <Incentive />
                  </Guard>
                }
              />
              <Route
                path="/follow_call_config"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <FollowCallConfig />
                  </Guard>
                }
              />
              <Route
                path="/outside_entry_site"
                element={
                  <Guard opts={{ denyDbAuthority: ["トスアップアカウント"] }}>
                    <OutsideEntrySite />
                  </Guard>
                }
              />
              <Route
                path="/column_config"
                element={
                  <Guard
                    opts={{
                      denyDbAuthority: [
                        "営業アカウント",
                        "トスアップアカウント",
                      ],
                    }}
                  >
                    <ScreenColumnConfig />
                  </Guard>
                }
              />
              <Route
                path="/partner"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                      ],
                    }}
                  >
                    <SaleCompany />
                  </Guard>
                }
              />
              <Route
                path="/config"
                element={
                  <Guard opts={{ allowAuthority: ["パートナー"] }}>
                    <SaleCompany />
                  </Guard>
                }
              />
              <Route
                path="/account"
                element={
                  <Guard
                    opts={{
                      allowAuthority: [
                        "マスターアカウント",
                        "スタッフアカウント",
                        "パートナー",
                      ],
                    }}
                  >
                    <Account />
                  </Guard>
                }
              />
              <Route
                path="/system_config"
                element={
                  <Guard opts={{ allowAuthority: ["マスターアカウント"] }}>
                    <SystemConfig />
                  </Guard>
                }
              />
              <Route path="/inquiry" element={<Inquiry />} />
            </Routes>
          </AppShell>
        </ThemeProvider>
      ) : (
        <ThemeProvider theme={poTheme}>
          <CssBaseline />
          <Login />
        </ThemeProvider>
      )}
    </>
  );
}

export default App;
